import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tab,
  Skeleton,
  Tabs,
  Divider,
} from "@mui/material";
import {
  Done,
  MoreHoriz,
  NotificationsOutlined,
  Visibility,
} from "@mui/icons-material";

import { useNotificationContext } from "contexts/NotificationContext";

import NotificationMessage from "./NotificationMessage";

import {
  useLazyGetNotificationListQuery,
  useNotificationReadAllMutation,
} from "store/services/notificationApi";

import { useAuth } from "hooks";

import { getSocketInstance } from "socket/socket.io";
import { NOTIFICATION_CONSTANTS } from "constants/notificationConstants";

import { toast } from "react-toastify";

import notificationSound from "assets/audio/bell.wav";

const Notifications = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [dotAnchorEl, setDotAnchorEl] = React.useState(null);

  const { state: notificationState, dispatch: notificationDispatch } =
    useNotificationContext();

  const [
    fetchNotification,
    { isLoading: loadingNotification, isFetching: fetchingNotification },
  ] = useLazyGetNotificationListQuery();

  const [onReadAll] = useNotificationReadAllMutation();

  const { loggedInUser, viewEntity, userEntities, isClient, isDriver, isVendor } = useAuth();

  const isBusy = loadingNotification || fetchingNotification;

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : "no-popover";

  const dotOpen = Boolean(dotAnchorEl);
  const dotId = dotOpen ? "dotPopover" : "hidden-dotPopover";

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDotClick = (event) => {
    setDotAnchorEl(event.currentTarget);
  };
  const handleDotClose = () => {
    setDotAnchorEl(null);
  };

  const handleChange = (event, newValue) => {
    notificationDispatch({
      type: NOTIFICATION_CONSTANTS.CHANGE_NOTIFICATION_TAB,
      payload: newValue,
    });
  };

  const onNotificationRead = (id) => {
    notificationDispatch({
      type: NOTIFICATION_CONSTANTS.READ_NOTIFICATION,
      payload: {
        id: id,
      },
    });
  };

  const onReadAllNotifications = async () => {
    await onReadAll();
    notificationDispatch({
      type: NOTIFICATION_CONSTANTS.READ_ALL_NOTIFICATION,
    });

    handleDotClose();
  };

  const onViewAllNotifications = async () => {
    handleDotClose();
    handleClose();
    if (isClient) {
      navigate("/app/client/notification/?tab=All");
    } else if (isDriver) {
      navigate("/app/driver/notification/list/?tab=All");
    }
    else if (isVendor) {
      navigate("/app/vendor/notification/list/?tab=All");
    } else {
      navigate("/app/crm/notification/list/?tab=All");
    }
  };

  const fetchData = React.useCallback(async () => {
    const data = await fetchNotification({
      isRead: notificationState.tab,
    }).unwrap();

    notificationDispatch({
      type: NOTIFICATION_CONSTANTS.SET_NOTIFICATION,
      payload: { data: data?.data ?? [], count: data?.unreadItems ?? 0 },
    });
  }, [notificationDispatch, fetchNotification, notificationState.tab]);

  React.useEffect(() => {
    if (userEntities) {
      fetchData();
    }
  }, [fetchData, userEntities]);

  const playNotificationSound = () => {
    const audio = new Audio(notificationSound);
    audio.play().catch((error) => console.error("Audio play failed", error));
  };

  React.useEffect(() => {
    const socket = getSocketInstance();

    if (!socket) return;

    socket.on(`notifications/${loggedInUser?._id}`, (data) => {
      const { intendedUsers, account, entity } = data;
      const _loggedInAccount = viewEntity?.entity?.account?._id;
      const _loggedInEntity = viewEntity?.entity?._id;

      const isUser = intendedUsers.some((data) => data === loggedInUser?._id);

      if (
        isUser &&
        _loggedInEntity === entity &&
        _loggedInAccount === account
      ) {
        notificationDispatch({
          type: NOTIFICATION_CONSTANTS.ADD_NEW_NOTIFICATION,
          payload: { newData: data },
        });
        toast.info(data.message || data.title, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        playNotificationSound();
      } else if (isClient && isUser) {
        notificationDispatch({
          type: NOTIFICATION_CONSTANTS.ADD_NEW_NOTIFICATION,
          payload: { newData: data },
        });
        toast.info(data.message || data.title, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        playNotificationSound();
      }
    });

    return () => socket.off(`notifications/${loggedInUser?._id}`);
  }, [
    isClient,
    loggedInUser?._id,
    notificationDispatch,
    viewEntity.authorization.level,
    viewEntity?.entity?._id,
    viewEntity?.entity?.account?._id,
  ]);

  return (
    <>
      <IconButton aria-label="" onClick={handleClick}>
        <Badge
          badgeContent={!loadingNotification ? notificationState.count : 0}
          color="primary"
          aria-describedby={id}
          variant="contained"
        >
          <NotificationsOutlined />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          className="  flex min-w-[300px] max-w-[400px] flex-col rounded-md pt-1 md:min-w-[400px] "
          sx={{
            height: "600px",
            overflow: "hidden",
          }}
        >
          <Box className="flex items-center  justify-between px-3 ">
            <p className=" text-[17px] font-bold text-slate-600 dark:text-slate-200">
              Notifications
            </p>

            <IconButton
              aria-label=""
              className="cursor-pointer "
              aria-describedby={dotId}
              variant="contained"
              onClick={handleDotClick}
            >
              <MoreHoriz />
            </IconButton>
          </Box>

          <Box
            className="sticky top-0 z-10"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tabs
              value={notificationState.tab}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="All" value="All" />
              <Tab label="Unread" value="unread" />
            </Tabs>
          </Box>

          <Divider />

          <Box className={" flex h-[500px] flex-col overflow-y-scroll "}>
            {!isBusy ? (
              notificationState?.data?.length === 0 ? (
                <p className="mt-5 text-center">No Data Found</p>
              ) : (
                notificationState?.data?.map((item) => {
                  return (
                    <NotificationMessage
                      key={item?._id}
                      {...item}
                      closePopover={handleClose}
                      onRead={onNotificationRead}
                    />
                  );
                })
              )
            ) : (
              [...Array(6)].map((item, index) => (
                <Box className="mx-3 flex items-center gap-1 py-2" key={index}>
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    animation="wave"
                  />

                  <Skeleton
                    variant="text"
                    sx={{ fontSize: "1rem" }}
                    className="w-full"
                    animation="wave"
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Popover>

      <Popover
        id={dotId}
        open={dotOpen}
        anchorEl={dotAnchorEl}
        onClose={handleDotClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          borderRadius: "20px",
        }}
      >
        <List disablePadding>
          <ListItem
            className="cursor-pointer flex-row p-2 hover:bg-blue-100 dark:hover:bg-slate-600"
            onClick={() => onReadAllNotifications()}
          >
            <ListItemIcon>
              <Done />
            </ListItemIcon>

            <ListItemText
              primary="Read all Message"
              className="cursor-pointer font-bold"
            />
          </ListItem>

          <ListItem
            className="cursor-pointer flex-row p-2 hover:bg-blue-100 dark:hover:bg-slate-600"
            onClick={onViewAllNotifications}
          >
            <ListItemIcon>
              <Visibility />
            </ListItemIcon>

            <ListItemText
              primary="View all Message"
              className="cursor-pointer font-bold"
            />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default Notifications;
